import { Injectable } from '@angular/core';

import { FsDataDogRumService } from '@lib/data-dog-rum';

/**
 * Service for monitoring Google Maps tile loading latency.
 *
 * Monitors tile loading performance for Google Maps via HTMLImageElement.src interception.
 * Logs slow requests, timeouts, and errors to DataDog for performance tracking.
 */
@Injectable({
  providedIn: 'root',
})
export class GoogleTileMonitorService {
  private static _isInitialized = false;

  private readonly TEN_SECONDS_MS = 10_000;
  private readonly FIFTEEN_SECONDS_MS = 15_000;

  private readonly GOOGLE_TILE_URL_PATTERNS = [
    /(?:maps|tile)\.googleapis\.com\b/i,
    /mapsresources.*\.googleapis\.com\/v1\/tiles/i,
    /gstatic\.com\/(?:maps|map|vt|tiles)\b/i,
    /mt\d\.google\.com\/vt\b/i,
    /khms\d\.google\.com\/kh\b/i,
    /\/v1\/tiles\//i,
  ];

  constructor(private readonly _datadogRumService: FsDataDogRumService) {}

  public initialize(): void {
    if (GoogleTileMonitorService._isInitialized) {
      return;
    }

    this._setupImageInterceptor();
    GoogleTileMonitorService._isInitialized = true;
  }

  private _isGoogleTileRequest(url: string): boolean {
    try {
      return this.GOOGLE_TILE_URL_PATTERNS.some((re) => re.test(url));
    } catch (_) {
      return false;
    }
  }

  private _logSlowRequest(
    url: string,
    durationMs: number,
    status = 0,
    timedOut = false,
    isError = false,
  ): void {
    const isSlow = durationMs >= this.TEN_SECONDS_MS;
    const isBlocked = status === 0 && !timedOut && isError;
    const shouldLog = isSlow || timedOut || isBlocked;

    if (shouldLog) {
      this._datadogRumService.addAction('map_tiles_timeout', {
        timeoutMs: durationMs,
        url,
        provider: 'google',
        failed: isError,
      });
    }
  }

  private _setupImageInterceptor(): void {
    const originalImageSrc = Object.getOwnPropertyDescriptor(HTMLImageElement.prototype, 'src');
    if (!originalImageSrc) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const service = this;

    Object.defineProperty(HTMLImageElement.prototype, 'src', {
      get: originalImageSrc.get,
      set: function (value: string) {
        if (value && service._isGoogleTileRequest(value)) {
          const start = Date.now();
          // eslint-disable-next-line prefer-const
          let timeoutId: number | undefined;
          let timedOut = false;
          // eslint-disable-next-line @typescript-eslint/no-this-alias
          const img = this;

          const cleanup = () => {
            if (timeoutId) {
              clearTimeout(timeoutId);
            }
            img.removeEventListener('load', onLoad);
            img.removeEventListener('error', onError);
          };

          const onLoad = () => {
            if (!timedOut) {
              const durationMs = Date.now() - start;
              service._logSlowRequest(value, durationMs, 200);
            }
            cleanup();
          };

          const onError = () => {
            if (!timedOut) {
              const durationMs = Date.now() - start;
              service._logSlowRequest(value, durationMs, 0, false, true);
            }
            cleanup();
          };

          const onTimeout = () => {
            timedOut = true;
            const durationMs = Date.now() - start;
            service._logSlowRequest(value, durationMs, 0, true);
            cleanup();
          };

          // Set up 15-second timeout
          timeoutId = window.setTimeout(onTimeout, service.FIFTEEN_SECONDS_MS);

          img.addEventListener('load', onLoad);
          img.addEventListener('error', onError);
        }

        return originalImageSrc.set?.call(this, value);
      },
      configurable: true,
      enumerable: true,
    });
  }
}
